import { loadGame } from '../games/GameRegistry.js';
import type { GameConfig, Unmount } from '../shared/Game.js';

type State = 'cover' | 'playing' | 'revealed';

function init(root: HTMLElement) {
  const configElement = document.getElementById('game-config');
  const stage = root.querySelector<HTMLElement>('.invite-game');
  const canvas = stage?.querySelector('canvas');
  const progress = stage?.querySelector<HTMLElement>('[data-progress]');
  const done = stage?.querySelector<HTMLElement>('[data-done]');
  const details = root.querySelector<HTMLElement>('#details');
  if (!configElement || !stage || !canvas || !progress || !done || !details) {
    return;
  }

  const config = JSON.parse(configElement.textContent ?? '{}') as GameConfig;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let unmount: Unmount | undefined;
  let session = 0;

  const setState = (state: State) => {
    root.dataset.state = state;
    stage.hidden = state !== 'playing';
    document.documentElement.style.overflow = state === 'playing' ? 'hidden' : '';
  };

  const reveal = () => {
    session++;
    unmount?.();
    unmount = undefined;
    setState('revealed');
    details.focus({ preventScroll: true });
    details.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  };

  const play = async () => {
    const current = ++session;
    done.hidden = true;
    progress.textContent = `0 / ${config.target}`;
    setState('playing');
    try {
      const game = await loadGame(config.gameId);
      if (current !== session) {
        return;
      }
      unmount = game.mount(canvas, {
        skin: config.skin,
        target: config.target,
        reducedMotion,
        onProgress: (count, total) => {
          progress.textContent = `${count} / ${total}`;
          if (count >= total) {
            done.hidden = false;
          }
        },
        onComplete: () => {
          if (current === session) {
            reveal();
          }
        },
      });
      canvas.focus({ preventScroll: true });
    } catch {
      reveal();
    }
  };

  root.addEventListener('click', (event) => {
    const action = (event.target as Element).closest<HTMLElement>('[data-action]')?.dataset.action;
    if (action === 'play') {
      void play();
    } else if (action === 'skip') {
      reveal();
    }
  });

  root.querySelector<HTMLFormElement>('[data-rsvp-form]')?.addEventListener('submit', (event) => {
    const form = event.currentTarget as HTMLFormElement;
    if (form.dataset.submitting === 'true') {
      event.preventDefault();
      return;
    }
    form.dataset.submitting = 'true';
    form.querySelector<HTMLButtonElement>('button[type="submit"]')?.setAttribute('aria-busy', 'true');
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root.dataset.state === 'playing') {
      reveal();
    }
  });
}

const root = document.querySelector<HTMLElement>('[data-invite]');
if (root) {
  init(root);
}
